import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  MessageSquare,
  Bot,
  Phone,
  ArrowRight,
  CheckCircle,
  Users,
  Target,
  Zap,
  Star,
  Sparkles,
  Brain,
  Shield,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Card, CardContent } from "./ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { toast } from "sonner";
import { Toaster } from "./ui/sonner";
import { mockData } from "../data/mock";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });

  const [registrationForm, setRegistrationForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/contacts`, contactForm);
      if (response.data.success) {
        toast.success(
          "Thank you! Your inquiry has been submitted successfully."
        );
        setContactForm({ name: "", email: "", company: "", message: "" });
      }
    } catch (error) {
      console.error("Contact submission error:", error);
      toast.error(
        "Failed to submit inquiry. Please try again or contact us directly."
      );
    }
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/register`, registrationForm);
      if (response.data.success) {
        toast.success(
          "Registration successful! Our team will contact you within 24 hours."
        );
        setRegistrationForm({ name: "", email: "", company: "", phone: "" });
      }
    } catch (error) {
      console.error("Registration error:", error);
      if (error.response?.status === 400) {
        toast.error(
          "Email already registered. Please use a different email or contact support."
        );
      } else {
        toast.error(
          "Failed to create account. Please try again or contact support."
        );
      }
    }
  };

  const handlePhoneClick = async () => {
    // Track phone click
    try {
      await axios.post(`${API}/track-phone-click`, {
        phone_number: "+19016316632",
        user_agent: navigator.userAgent,
      });
    } catch (error) {
      console.error("Phone click tracking failed:", error);
    }
    // Open phone dialer
    window.open("tel:+19016316632", "_self");
  };

  const handleEmailClick = async () => {
    // Track email click and open email client
    window.open("mailto:info@letsOptimize.ai", "_self");
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut" },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const scaleIn = {
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.5, ease: "easeOut" },
  };

  return (
    <div className="min-h-screen bg-page">
      <Toaster />

      {/* Header Navigation */}
      <header className="header-nav">
        <div className="container">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <h1 className="heading-2 font-main">Let's Optimize</h1>
            </div>
            <nav className="nav-actions hidden md:flex">
              <a href="#services" className="btn-tag">
                Services
              </a>
              <a href="#about" className="btn-tag">
                About
              </a>
              <a href="#contact" className="btn-tag">
                Contact
              </a>
              <a href="#register" className="btn-primary">
                Contact Us
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <motion.section
        className="hero-section"
        initial="initial"
        animate="animate"
        variants={staggerContainer}
      >
        <div className="hero-content">
          <motion.div className="ai-feature-badge mx-auto" variants={fadeInUp}>
            <Sparkles className="w-4 h-4" />
            <span>Next-Gen AI Customer Engagement</span>
          </motion.div>

          <motion.h1 className="heading-hero mb-6" variants={fadeInUp}>
            Transform Your Business with
            <br />
            <span className="gradient-text">Intelligent AI Agents</span>
          </motion.h1>

          <motion.p
            className="body-large mb-8 max-w-3xl mx-auto"
            variants={fadeInUp}
          >
            Revolutionary AI-powered customer service that learns, adapts, and
            delivers exceptional experiences across email, SMS, chat, and voice
            interactions. Scale your business without limits.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            variants={fadeInUp}
          >
            <a href="#register" className="btn-primary">
              Get Started Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
            <a href="#services" className="btn-secondary">
              Learn More
            </a>
          </motion.div>

          {/* Hero AI Image - Changed to AI Circuit Brain */}
          <motion.div
            className="mt-16 flex justify-center"
            variants={fadeInUp}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01"
                alt="AI Technology and Intelligence"
                className="ai-image w-80 h-80 object-cover rounded-2xl shadow-2xl ai-pulse"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent rounded-2xl"></div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-section">
        <div className="container">
          <motion.div
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            variants={fadeInUp}
            viewport={{ once: true }}
          >
            <h2 className="heading-1 mb-6">Our AI-Powered Solutions</h2>
            <p className="body-large text-secondary max-w-3xl mx-auto">
              Comprehensive AI agents designed to revolutionize every aspect of
              your customer journey with human-like intelligence and 24/7
              availability.
            </p>
          </motion.div>

          <motion.div
            className="ai-services-grid"
            initial="initial"
            whileInView="animate"
            variants={staggerContainer}
            viewport={{ once: true }}
          >
            {mockData.services.map((service, index) => (
              <motion.div key={service.id} variants={scaleIn}>
                <Card
                  className={`ai-service-card accent-${service.color} hover-lift`}
                >
                  <CardContent className="p-0">
                    <div className="flex items-start mb-6">
                      <div
                        className={`w-16 h-16 rounded-xl bg-gradient-to-r from-${service.color}-500 to-${service.color}-600 flex items-center justify-center mr-4 shadow-lg`}
                      >
                        <service.icon className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="heading-3 mb-2">{service.name}</h3>
                        <div className="caption text-purple-400">
                          {service.category}
                        </div>
                      </div>
                    </div>

                    <p className="body-medium text-secondary mb-6">
                      {service.description}
                    </p>

                    <div className="space-y-3 mb-6">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center">
                          <CheckCircle className="w-5 h-5 mr-3 text-green-400 flex-shrink-0" />
                          <span className="body-small">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Service Image */}
                    <div className="mb-6">
                      <img
                        src={service.image}
                        alt={service.name}
                        className="ai-image w-full h-48 object-cover rounded-lg"
                      />
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                      <div className="caption">
                        Learn More About {service.shortName}
                      </div>
                      <ArrowRight className="w-5 h-5 text-purple-400" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="about" className="py-20 bg-page">
        <div className="container">
          <motion.div
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            variants={fadeInUp}
            viewport={{ once: true }}
          >
            <h2 className="heading-1 mb-6">Why Industry Leaders Choose Us</h2>
            <p className="body-large text-secondary max-w-3xl mx-auto">
              Experience the competitive advantage of AI-driven customer
              engagement that delivers measurable results and transforms your
              business operations.
            </p>
          </motion.div>

          <motion.div
            className="ai-grid"
            initial="initial"
            whileInView="animate"
            variants={staggerContainer}
            viewport={{ once: true }}
          >
            {mockData.benefits.map((benefit, index) => (
              <motion.div key={benefit.id} variants={scaleIn}>
                <Card className="ai-service-card hover-lift bg-card">
                  <CardContent className="text-center">
                    <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
                      <benefit.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="heading-3 mb-4">{benefit.title}</h3>
                    <p className="body-medium text-secondary mb-4">
                      {benefit.description}
                    </p>
                    <div className="caption text-purple-400">
                      {benefit.metric}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-section">
        <div className="container max-w-4xl">
          <motion.div
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            variants={fadeInUp}
            viewport={{ once: true }}
          >
            <h2 className="heading-1 mb-6">Frequently Asked Questions</h2>
            <p className="body-large text-secondary">
              Get answers to common questions about our AI-powered customer
              service solutions.
            </p>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            variants={fadeInUp}
            viewport={{ once: true }}
          >
            <Accordion type="single" collapsible className="space-y-4">
              {mockData.faqs.map((faq, index) => (
                <AccordionItem
                  key={faq.id}
                  value={`item-${index}`}
                  className="modern-form"
                >
                  <AccordionTrigger className="text-left heading-3 px-6 py-4 hover:no-underline text-primary">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 body-medium text-secondary">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Registration Section */}
      <section id="register" className="py-20 bg-page">
        <div className="container max-w-4xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="initial"
              whileInView="animate"
              variants={fadeInUp}
              viewport={{ once: true }}
            >
              <div className="ai-feature-badge mb-6">
                <Shield className="w-4 h-4" />
                <span>Enterprise-Ready AI</span>
              </div>

              <h2 className="heading-1 mb-6">
                Ready to Transform Your Customer Experience?
              </h2>
              <p className="body-large text-secondary mb-8">
                Join hundreds of businesses already leveraging AI to deliver
                exceptional customer service and drive unprecedented growth.
              </p>

              <div className="space-y-4">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span className="body-medium">Custom AI solutions</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span className="body-medium">
                    Expert consultation included
                  </span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span className="body-medium">Setup in under 5 minutes</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="initial"
              whileInView="animate"
              variants={scaleIn}
              viewport={{ once: true }}
            >
              <Card className="modern-form">
                <CardContent>
                  <form
                    onSubmit={handleRegistrationSubmit}
                    className="space-y-6"
                  >
                    <div>
                      <label className="caption mb-2 block">Full Name</label>
                      <Input
                        type="text"
                        value={registrationForm.name}
                        onChange={(e) =>
                          setRegistrationForm({
                            ...registrationForm,
                            name: e.target.value,
                          })
                        }
                        required
                        className="bg-bg-page border-border-input focus:border-border-input-focus text-gray-900"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label className="caption mb-2 block">
                        Email Address
                      </label>
                      <Input
                        type="email"
                        value={registrationForm.email}
                        onChange={(e) =>
                          setRegistrationForm({
                            ...registrationForm,
                            email: e.target.value,
                          })
                        }
                        required
                        className="bg-bg-page border-border-input focus:border-border-input-focus text-gray-900"
                        placeholder="Enter your email"
                      />
                    </div>
                    <div>
                      <label className="caption mb-2 block">Company Name</label>
                      <Input
                        type="text"
                        value={registrationForm.company}
                        onChange={(e) =>
                          setRegistrationForm({
                            ...registrationForm,
                            company: e.target.value,
                          })
                        }
                        required
                        className="bg-bg-page border-border-input focus:border-border-input-focus text-gray-900"
                        placeholder="Enter your company"
                      />
                    </div>
                    <div>
                      <label className="caption mb-2 block">Phone Number</label>
                      <Input
                        type="tel"
                        value={registrationForm.phone}
                        onChange={(e) =>
                          setRegistrationForm({
                            ...registrationForm,
                            phone: e.target.value,
                          })
                        }
                        className="bg-bg-page border-border-input focus:border-border-input-focus text-gray-900"
                        placeholder="Enter your phone number"
                      />
                    </div>
                    <Button type="submit" className="btn-primary w-full">
                      Get Started Now
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-section">
        <div className="container max-w-4xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="initial"
              whileInView="animate"
              variants={fadeInUp}
              viewport={{ once: true }}
            >
              <h2 className="heading-1 mb-6">
                Let's Build the Future Together
              </h2>
              <p className="body-large text-secondary mb-8">
                Ready to discuss how our AI solutions can transform your
                business? Our experts are here to help you design the perfect
                customer experience strategy.
              </p>

              <div className="space-y-4 mb-8">
            

                <div
                  className="flex items-center p-4 bg-card rounded-lg border border-border-primary hover:border-purple-400 cursor-pointer transition-all duration-300"
                  onClick={handleEmailClick}
                >
                  <Mail className="w-5 h-5 text-purple-400 mr-3" />
                  <div>
                    <p className="body-small text-muted">Email our team</p>
                    <p className="body-medium text-primary">
                      info@letsOptimize.ai
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <img
                  src="https://images.unsplash.com/photo-1694903089438-bf28d4697d9a"
                  alt="AI Human Collaboration"
                  className="ai-image w-full h-64 object-cover rounded-lg"
                />
              </div>
            </motion.div>

            <motion.div
              initial="initial"
              whileInView="animate"
              variants={scaleIn}
              viewport={{ once: true }}
            >
              <Card className="modern-form">
                <CardContent>
                  <form onSubmit={handleContactSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="caption mb-2 block">Name</label>
                        <Input
                          type="text"
                          value={contactForm.name}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              name: e.target.value,
                            })
                          }
                          required
                          className="bg-bg-page border-border-input focus:border-border-input-focus text-gray-900"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className="caption mb-2 block">Email</label>
                        <Input
                          type="email"
                          value={contactForm.email}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              email: e.target.value,
                            })
                          }
                          required
                          className="bg-bg-page border-border-input focus:border-border-input-focus text-gray-900"
                          placeholder="Your email"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="caption mb-2 block">Company</label>
                      <Input
                        type="text"
                        value={contactForm.company}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            company: e.target.value,
                          })
                        }
                        className="bg-bg-page border-border-input focus:border-border-input-focus text-gray-900"
                        placeholder="Your company"
                      />
                    </div>
                    <div>
                      <label className="caption mb-2 block">Message</label>
                      <Textarea
                        value={contactForm.message}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            message: e.target.value,
                          })
                        }
                        rows={4}
                        required
                        className="bg-bg-page border-border-input focus:border-border-input-focus text-gray-900"
                        placeholder="Tell us about your needs..."
                      />
                    </div>
                    <Button type="submit" className="btn-primary w-full">
                      Send Message
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border-primary py-12">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                  <Brain className="w-5 h-5 text-white" />
                </div>
                <h3 className="heading-3 font-main">Let's Optimize</h3>
              </div>
              <p className="body-small text-muted">
                Transforming customer experiences with next-generation AI
                intelligence.
              </p>
            </div>
            <div>
              <h4 className="caption mb-4">AI Services</h4>
              <ul className="space-y-2 body-small text-muted">
                <li>AI Marketing Emails</li>
                <li>Smart SMS Chat</li>
                <li>Intelligent Chatbot</li>
                <li>Voice Sales Agent</li>
              </ul>
            </div>
            <div>
              <h4 className="caption mb-4">Company</h4>
              <ul className="space-y-2 body-small text-muted">
                <li>About Us</li>
                <li>Careers</li>
                <li>Privacy Policy</li>
                <li>Terms of Service</li>
              </ul>
            </div>
            <div>
              <h4 className="caption mb-4">Contact</h4>
              <ul className="space-y-2 body-small text-muted">
                <li
                  className="cursor-pointer hover:text-purple-400 transition-colors"
                  onClick={handleEmailClick}
                >
                  info@letsOptimize.ai
                </li>

                <li>San Francisco, CA</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border-primary mt-8 pt-8 text-center">
            <p className="body-small text-muted">
              © 2025 Let's Optimize. All rights reserved. Powered by Advanced AI
              Technology.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
